"use client"

import { notify } from "@/lib/notifications/notify"
import { useState, useEffect, useCallback } from "react"
import { useRouter } from "next/navigation"
import { createClient } from "@/lib/supabase/client"
import { Input } from "@/components/ui/input"
import { Search, Filter, Loader2, Users, Check } from "lucide-react"
import { toast } from "sonner"
import { readActiveShopId } from "@/lib/active-shop"
import { fetchTalent } from "@/lib/talent/fetch-talent"
import { InquireModal } from "@/components/network/inquire-modal"
import {
  TalentCard, CityFilterCombobox, type BarberCard,
  CHIP, CHIP_BASE, FILTER_AVAILABILITY, FILTER_CLIENTELE, FILTER_PAYMENT,
} from "@/components/network/talent-card"

type OwnerShop = { id: string; name: string; city: string | null; state: string | null; logo_url: string | null; cover_image: string | null } | null

// Self-contained "source talent" feed — the exact Community/Network Talent
// column, packaged so the owner Hiring page can drop it in. Owns its own auth,
// active-shop, follow state, and barber fetch so it carries everywhere.
export function TalentFeed() {
  const router = useRouter()
  const supabase = createClient()

  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const [ownerShop, setOwnerShop] = useState<OwnerShop>(null)
  const [followingIds, setFollowingIds] = useState<Set<string>>(new Set())
  const [followLoading, setFollowLoading] = useState<string | null>(null)

  const [barbers, setBarbers] = useState<BarberCard[]>([])
  const [talentLoading, setTalentLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [cityFilter, setCityFilter] = useState("")
  const [availabilityFilters, setAvailabilityFilters] = useState<string[]>([])
  const [clienteleFilters, setClienteleFilters] = useState<string[]>([])
  const [paymentFilters, setPaymentFilters] = useState<string[]>([])
  const [openToWorkOnly, setOpenToWorkOnly] = useState(false)
  const [creatorOnly, setCreatorOnly] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [inviteLoading] = useState<string | null>(null)
  const [inquireTarget, setInquireTarget] = useState<{ id: string; name: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user || cancelled) return
        setCurrentUserId(user.id)
        const { data: shops } = await supabase
          .from("shops").select("id, name, city, state, logo_url, cover_image")
          .eq("owner_id", user.id).eq("is_published", true)
          .order("created_at", { ascending: true })
        if (shops && shops.length > 0 && !cancelled) {
          const stored = readActiveShopId()
          const active = (stored && shops.find((s: any) => s.id === stored)) || shops[0]
          setOwnerShop(active as OwnerShop)
        }
        const { data: follows } = await supabase.from("network_follows").select("following_id").eq("follower_id", user.id)
        if (!cancelled) setFollowingIds(new Set((follows || []).map((f: any) => f.following_id)))
      } catch (e) {
        console.warn("[talent-feed] init failed", e)
      }
    })()
    return () => { cancelled = true }
  }, [supabase])

  const fetchBarbers = useCallback(async () => {
    setTalentLoading(true)
    try {
      // Shared with the owner Network page: same roster, same order (photos
      // first, then most active). See lib/talent/fetch-talent.ts and rank.ts.
      setBarbers(
        await fetchTalent(supabase, {
          search,
          city: cityFilter,
          openToWorkOnly,
          creatorOnly,
          availability: availabilityFilters,
          clientele: clienteleFilters,
          payment: paymentFilters,
        }),
      )
    } catch (e) {
      console.warn("[talent-feed] fetchBarbers failed", e)
    } finally {
      setTalentLoading(false)
    }
  }, [search, cityFilter, availabilityFilters, clienteleFilters, paymentFilters, openToWorkOnly, creatorOnly, supabase])

  useEffect(() => {
    const t = setTimeout(fetchBarbers, 300)
    return () => clearTimeout(t)
  }, [fetchBarbers])

  const openInquire = (barberId: string, barberName: string) => {
    if (!currentUserId) return
    setInquireTarget({ id: barberId, name: barberName })
  }

  const handleMessage = (barberId: string) => {
    router.push(`/dashboard/owner/messages?userId=${barberId}`)
  }

  const handleFollow = async (barberId: string) => {
    if (!currentUserId) return
    if (barberId === currentUserId) {
      toast.info("That's your own post — you can't follow yourself.")
      return
    }
    setFollowLoading(barberId)
    if (followingIds.has(barberId)) {
      await supabase.from("network_follows").delete().eq("follower_id", currentUserId).eq("following_id", barberId)
      setFollowingIds(prev => { const s = new Set(prev); s.delete(barberId); return s })
    } else {
      await supabase.from("network_follows").insert({ follower_id: currentUserId, following_id: barberId })
      setFollowingIds(prev => new Set([...prev, barberId]))
      toast.success("Following")
      const { data: follower } = await supabase.from("profiles").select("full_name, role").eq("id", currentUserId).maybeSingle()
      const followerName = (follower as any)?.full_name?.trim() || "An owner"
      const shopName = ownerShop?.name
      await notify({
        recipient: barberId,
        type: "new_follower",
        shopId: ownerShop?.id,
        metadata: {
          follower_id: currentUserId,
          follower_role: (follower as any)?.role || "owner",
          shop_id: ownerShop?.id,
          shop_name: shopName,
        },
      })
    }
    setFollowLoading(null)
  }

  const activeFilterCount =
    (openToWorkOnly ? 1 : 0) +
    (creatorOnly ? 1 : 0) +
    (cityFilter.trim() ? 1 : 0) +
    availabilityFilters.length +
    clienteleFilters.length +
    paymentFilters.length
  const hasActiveFilters = activeFilterCount > 0

  return (
    <div className="space-y-4">
      <InquireModal
        open={!!inquireTarget}
        onClose={() => setInquireTarget(null)}
        barberId={inquireTarget?.id || ""}
        barberName={inquireTarget?.name || ""}
        ownerId={currentUserId}
        ownerShop={ownerShop}
      />

      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold text-base">Talent</h3>
        <div className="flex items-center gap-3">
          {hasActiveFilters && (
            <button onClick={() => { setCityFilter(""); setOpenToWorkOnly(false); setCreatorOnly(false); setAvailabilityFilters([]); setClienteleFilters([]); setPaymentFilters([]) }}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              Clear
            </button>
          )}
          <button
            onClick={() => setShowFilters(v => !v)}
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 h-8 rounded-full border transition-colors ${
              hasActiveFilters || showFilters
                ? "border-foreground bg-foreground text-background"
                : "border-border hover:bg-muted text-muted-foreground"
            }`}
            aria-expanded={showFilters}
          >
            <Filter className="w-3.5 h-3.5" />
            {showFilters ? "Hide filters" : "Filters"}
            {activeFilterCount > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-background/20 text-[10px] font-semibold leading-none">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search barbers…"
          className="pl-9 rounded-full bg-muted/50 border-border h-9"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {showFilters && (
        <div className="space-y-4 pt-4 pb-2">
          <CityFilterCombobox
            value={cityFilter}
            onChange={setCityFilter}
            cities={Array.from(new Set(barbers.map(b => b.city).filter((c): c is string => !!c))).sort()}
          />

          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => setOpenToWorkOnly(v => !v)} className={`${CHIP_BASE} ${openToWorkOnly ? CHIP.emerald.on : CHIP.emerald.off}`}>
              {openToWorkOnly && <Check className="w-3 h-3 -ml-0.5" strokeWidth={2.5} />}
              Open to work
            </button>
            <button onClick={() => setCreatorOnly(v => !v)} className={`${CHIP_BASE} ${creatorOnly ? CHIP.fuchsia.on : CHIP.fuchsia.off}`}>
              {creatorOnly && <Check className="w-3 h-3 -ml-0.5" strokeWidth={2.5} />}
              Creator
            </button>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {FILTER_AVAILABILITY.map(f => {
              const selected = availabilityFilters.includes(f.value)
              return (
                <button key={f.value} onClick={() => setAvailabilityFilters(prev => selected ? prev.filter(v => v !== f.value) : [...prev, f.value])} className={`${CHIP_BASE} ${selected ? CHIP.amber.on : CHIP.amber.off}`}>
                  {selected && <Check className="w-3 h-3 -ml-0.5" strokeWidth={2.5} />}
                  {f.label}
                </button>
              )
            })}
          </div>

          <div className="flex flex-wrap gap-1.5">
            {FILTER_CLIENTELE.map(f => {
              const selected = clienteleFilters.includes(f.value)
              return (
                <button key={f.value} onClick={() => setClienteleFilters(prev => selected ? prev.filter(v => v !== f.value) : [...prev, f.value])} className={`${CHIP_BASE} ${selected ? CHIP.violet.on : CHIP.violet.off}`}>
                  {selected && <Check className="w-3 h-3 -ml-0.5" strokeWidth={2.5} />}
                  {f.label}
                </button>
              )
            })}
          </div>

          <div className="flex flex-wrap gap-1.5">
            {FILTER_PAYMENT.map(f => {
              const selected = paymentFilters.includes(f.value)
              return (
                <button key={f.value} onClick={() => setPaymentFilters(prev => selected ? prev.filter(v => v !== f.value) : [...prev, f.value])} className={`${CHIP_BASE} ${selected ? CHIP.sky.on : CHIP.sky.off}`}>
                  {selected && <Check className="w-3 h-3 -ml-0.5" strokeWidth={2.5} />}
                  {f.label}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {talentLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : barbers.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-[2.5rem]">
          <Users className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm font-semibold">No barbers found</p>
          <p className="text-xs text-muted-foreground mt-1">Try adjusting your filters</p>
        </div>
      ) : (
        <div className="divide-y divide-border/60 mt-4">
          {barbers.map(barber => (
            <div key={barber.id} className="pt-6 first:pt-6">
              <TalentCard
                barber={barber}
                isSelf={barber.id === currentUserId}
                inviteLoading={inviteLoading === barber.id}
                isFollowing={followingIds.has(barber.id)}
                followLoading={followLoading === barber.id}
                onFollow={() => handleFollow(barber.id)}
                onMessage={() => handleMessage(barber.id)}
                onInvite={() => openInquire(barber.id, barber.profiles?.full_name || "there")}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
